import { useCallback, useEffect, useState } from "react";
import { api, errDetail, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { BrainCircuit, ShieldCheck } from "lucide-react";

const usd = (n) => `$${Number(n || 0).toFixed(4)}`;

export default function AiGovernance() {
  const [usage, setUsage] = useState(null);
  const [cfg, setCfg] = useState({ use_cases: [] });
  const [calls, setCalls] = useState([]);
  const [caps, setCaps] = useState([]);
  const [edit, setEdit] = useState({});
  const load = useCallback(() => {
    api.get("/srm/ai/usage").then((r) => setUsage(r.data)).catch((e) => toast.error(errDetail(e)));
    api.get("/srm/ai/calls").then((r) => setCalls(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/srm/ai/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/srm/my-caps").then((r) => setCaps(r.data.caps || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const isAdmin = caps.includes("srm_admin");
  const ruleFor = (k) => (cfg.use_cases.find((u) => u.key === k) || {});
  const saveBudget = async (uc) => {
    try { await api.put("/srm/ai/budgets", { use_case: uc, monthly_budget_usd: Number(edit[uc]) }); toast.success("Budget gespeichert"); setEdit((e) => ({ ...e, [uc]: undefined })); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  if (!usage) return <div className="p-6 text-gray-400">Lädt…</div>;
  return (
    <div className="p-6 max-w-[1300px] mx-auto" data-testid="ai-governance-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><BrainCircuit className="h-5 w-5 text-[#F5A623]" /></div>
        <div><h1 className="text-2xl font-bold text-[#1B2A4A]">AI-Governance & Kosten</h1><p className="text-sm text-gray-500">Token-Kosten je Use-Case · Monatsbudget · Guard-Rails</p></div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex items-center justify-between" data-testid="ai-total">
        <div><div className="text-xs text-gray-500">Kosten diesen Monat</div><div className="text-2xl font-bold text-[#1B2A4A]">{usd(usage.total_usd)}</div></div>
        <ShieldCheck className="h-8 w-8 text-green-500" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {usage.by_use_case.map((u) => {
          const rule = ruleFor(u.use_case);
          const barColor = u.hard_stop ? "bg-red-500" : u.soft_warning ? "bg-amber-500" : "bg-[#1B2A4A]";
          return (
            <div key={u.use_case} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`ai-uc-${u.use_case}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-[#1B2A4A]">{rule.label || u.use_case}</div>
                <div className="flex gap-1">
                  {rule.human_confirm && <Badge className="bg-blue-100 text-blue-700">Human-Confirm</Badge>}
                  {u.hard_stop && <Badge className="bg-red-100 text-red-700">gesperrt</Badge>}
                  {!u.hard_stop && u.soft_warning && <Badge className="bg-amber-100 text-amber-800">80%+</Badge>}
                </div>
              </div>
              <div className="text-xs text-gray-400 mb-2">{rule.rule}</div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden mb-1"><div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, u.pct)}%` }} /></div>
              <div className="flex justify-between text-xs text-gray-500"><span>{usd(u.spent_usd)} / ${u.budget_usd.toFixed(2)}</span><span>{u.pct}% · {u.calls} Calls</span></div>
              {isAdmin && (
                <div className="flex items-center gap-2 mt-3">
                  <Input className="h-8 w-28" type="number" placeholder="Budget $" data-testid={`ai-budget-input-${u.use_case}`} value={edit[u.use_case] ?? ""} onChange={(e) => setEdit((s) => ({ ...s, [u.use_case]: e.target.value }))} />
                  <Button size="sm" className="h-8" data-testid={`ai-budget-save-${u.use_case}`} disabled={edit[u.use_case] === undefined || edit[u.use_case] === ""} onClick={() => saveBudget(u.use_case)}>Setzen</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <h3 className="font-semibold text-[#1B2A4A] px-4 py-3 border-b border-gray-100">Aufruf-Protokoll (ai_call_log)</h3>
        <Table><TableHeader><TableRow><TableHead>Use-Case</TableHead><TableHead>Modell</TableHead><TableHead className="text-right">Tokens</TableHead><TableHead className="text-right">Kosten</TableHead><TableHead>User</TableHead><TableHead>Zeit</TableHead></TableRow></TableHeader>
          <TableBody>
            {calls.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">Noch keine AI-Aufrufe</TableCell></TableRow>}
            {calls.map((c) => (
              <TableRow key={c.id} data-testid={`ai-call-${c.id}`}><TableCell>{c.use_case}</TableCell><TableCell>{c.model}</TableCell>
                <TableCell className="text-right text-sm">{c.input_tokens}/{c.output_tokens}</TableCell><TableCell className="text-right">{usd(c.cost_usd)}</TableCell>
                <TableCell>{c.user_name}</TableCell><TableCell className="text-sm text-gray-500">{fmtDate(c.at)}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
